import { debounce } from "@excalidraw/common";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import { appJotaiStore, currentCustomFileAtom } from "../app-jotai";

import { saveFile } from "./customFileApi";
import { generateThumbnail } from "./thumbnailGenerator";

import type {
  CustomFileData,
  CurrentCustomFile,
} from "../types/customFileLibrary";

const AUTOSAVE_DEBOUNCE_MS = 300;
const CURRENT_FILE_STORAGE_KEY = "excalidraw-custom-file-current";

class CustomFileStorageClass {
  private _autosave: ReturnType<typeof debounce>;

  constructor() {
    // Create debounced autosave function
    this._autosave = debounce(
      async (
        elements: readonly ExcalidrawElement[],
        appState: AppState,
        files: BinaryFiles,
      ) => {
        try {
          await this.performSave(elements, appState, files);
        } catch (error) {
          console.error(
            "Autosave to backend failed. Check your connection and authentication.",
            error,
          );
        }
      },
      AUTOSAVE_DEBOUNCE_MS,
    );
  }

  /**
   * Trigger autosave with debouncing
   * Skips if canvas is empty
   */
  public autosave(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ): void {
    // Skip if no elements
    if (!elements || elements.length === 0) {
      return;
    }

    this._autosave(elements, appState, files);
  }

  /**
   * Force immediate save (flushes debounce)
   */
  public async flushSave(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ): Promise<void> {
    if (!elements || elements.length === 0) {
      return;
    }

    await this.performSave(elements, appState, files);
  }

  /**
   * Perform the actual save operation
   */
  private async performSave(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ): Promise<void> {
    // Get current file info from atom (or restore from localStorage)
    let currentFile = appJotaiStore.get(currentCustomFileAtom);

    // If no current file in atom, try to restore from localStorage
    if (!currentFile.id) {
      currentFile = this.loadCurrentFileFromStorage();
    }

    // Generate ID if this is still a new file
    const fileId = currentFile.id || this.generateId();

    // Generate auto name from first text element if "Untitled"
    let fileName = currentFile.name;
    if (!fileName || fileName === "Untitled") {
      fileName = this.generateAutoName(elements);
    }

    // Generate thumbnail
    const thumbnail = await generateThumbnail(elements, appState, files);

    // Sanitize appState to remove transient properties
    const sanitizedAppState = this.sanitizeAppState(appState);

    // Create file data
    const fileData: CustomFileData = {
      id: fileId,
      name: fileName,
      thumbnail,
      elements: elements as ExcalidrawElement[],
      appState: sanitizedAppState,
      files,
      lastModified: new Date().toISOString(),
      createdAt: currentFile.id
        ? currentFile.lastSaved || new Date().toISOString()
        : new Date().toISOString(),
      folderId: currentFile.folderId || null,
    };

    // Save to API
    await saveFile(fileData);

    // Update current file tracking
    const updatedFile = {
      id: fileId,
      name: fileName,
      lastSaved: fileData.lastModified,
      folderId: currentFile.folderId || null,
    };

    // Update atom
    appJotaiStore.set(currentCustomFileAtom, updatedFile);

    // Persist to localStorage
    this.saveCurrentFileToStorage(updatedFile);
  }

  /**
   * Generate a unique file ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Generate an auto name from the first text element
   * Falls back to a date-based name
   */
  private generateAutoName(elements: readonly ExcalidrawElement[]): string {
    // Find first text element
    const textElement = elements.find((el) => el.type === "text" && el.text);

    if (textElement && "text" in textElement && textElement.text) {
      // Use first 30 characters of text
      const text = textElement.text.trim();
      return text.length > 30 ? `${text.substring(0, 30)}...` : text;
    }

    // Fallback to date-based name
    const now = new Date();
    return `Drawing ${now.toLocaleDateString()} ${now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }

  /**
   * Sanitize appState to remove transient properties
   */
  private sanitizeAppState(appState: AppState): Partial<AppState> {
    // Keep only persistent app state properties
    // Remove transient UI state and non-serializable properties
    const {
      cursorButton,
      draggingElement,
      editingElement,
      editingGroupId,
      editingLinearElement,
      isBindingEnabled,
      isDragging,
      isResizing,
      isRotating,
      lastPointerDownWith,
      multiElement,
      resizingElement,
      scrolledOutside,
      selectedElementIds,
      selectedGroupIds,
      selectionElement,
      startBoundElement,
      suggestedBindings,
      collaborators, // Remove - will be restored from current state
      ...cleanAppState
    } = appState as any;

    return cleanAppState;
  }

  /**
   * Save current file info to localStorage
   */
  private saveCurrentFileToStorage(currentFile: CurrentCustomFile): void {
    try {
      localStorage.setItem(
        CURRENT_FILE_STORAGE_KEY,
        JSON.stringify(currentFile),
      );
    } catch (error) {
      console.error("Failed to save current file to localStorage:", error);
    }
  }

  /**
   * Load current file info from localStorage
   */
  private loadCurrentFileFromStorage(): CurrentCustomFile {
    try {
      const stored = localStorage.getItem(CURRENT_FILE_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load current file from localStorage:", error);
    }
    return { id: null, name: "Untitled", lastSaved: null, folderId: null };
  }

  /**
   * Set the current file (called when opening a file)
   */
  public setCurrentFile(currentFile: CurrentCustomFile): void {
    // Cancel any pending save for the previous file before switching
    this._autosave.cancel();
    appJotaiStore.set(currentCustomFileAtom, currentFile);
    this.saveCurrentFileToStorage(currentFile);
  }

  /**
   * Get the current file info
   */
  public getCurrentFile(): CurrentCustomFile {
    const currentFile = appJotaiStore.get(currentCustomFileAtom);
    if (!currentFile.id) {
      return this.loadCurrentFileFromStorage();
    }
    return currentFile;
  }

  /**
   * Cancel any pending debounced autosave.
   * Call this before switching files to prevent a stale save from firing.
   */
  public cancelPendingAutosave(): void {
    this._autosave.cancel();
  }

  /**
   * Clear current file (create new file)
   */
  public clearCurrentFile(): void {
    // Cancel any pending save for the previous file first
    this._autosave.cancel();

    const emptyFile = {
      id: null,
      name: "Untitled",
      lastSaved: null,
      folderId: null,
    };
    appJotaiStore.set(currentCustomFileAtom, emptyFile);
    this.saveCurrentFileToStorage(emptyFile);
  }
}

// Export singleton instance
export const CustomFileStorage = new CustomFileStorageClass();
